import { ChevronLeft, ChevronRight, Clock3, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import Lang from '../../../config/lang'
import ApplicationNotice from './ApplicationNotice'

const DAY_LABELS = {
  id: ['Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab', 'Min'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
}

const TIME_OPTIONS = Array.from({ length: 10 }, (_, index) => {
  const hour = String(index + 8).padStart(2, '0')
  return `${hour}:00`
})

const isValidDate = (value) =>
  value instanceof Date && !Number.isNaN(value.getTime())

const toStartOfDay = (value) =>
  new Date(value.getFullYear(), value.getMonth(), value.getDate())

const parseDateValue = (value) => {
  if (!value) return null

  if (value instanceof Date) {
    return isValidDate(value) ? value : null
  }

  if (typeof value != 'string') return null

  const dateText = value.trim()
  if (!dateText) return null

  const dateOnlyMatch = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/)

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))
    return isValidDate(parsedDate) ? parsedDate : null
  }

  const fallbackDate = new Date(dateText)
  return isValidDate(fallbackDate) ? fallbackDate : null
}

const toDateKey = (value) => {
  const parsedDate = parseDateValue(value)

  if (!isValidDate(parsedDate)) return ''

  return `${parsedDate.getFullYear()}-${String(
    parsedDate.getMonth() + 1,
  ).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`
}

const isWeekend = (value) => {
  const parsedDate = parseDateValue(value)

  if (!isValidDate(parsedDate)) return false

  const dayIndex = parsedDate.getDay()
  return dayIndex == 0 || dayIndex == 6
}

const isBeforeDate = (value, compareDate) => {
  const parsedDate = parseDateValue(value)

  if (!isValidDate(parsedDate) || !isValidDate(compareDate)) return false

  return (
    toStartOfDay(parsedDate).getTime() < toStartOfDay(compareDate).getTime()
  )
}

const normalizeHolidayDateSet = (holidayDates = []) => {
  return new Set(
    holidayDates
      .map((item) => toDateKey(item))
      .filter((item) => String(item).trim() != ''),
  )
}

const normalizeBlockedDateSet = (blockedDateKeys = []) => {
  return new Set(
    blockedDateKeys
      .map((item) => toDateKey(item))
      .filter((item) => String(item).trim() != ''),
  )
}

const isHoliday = (value, holidayDateSet) => {
  const dateKey = toDateKey(value)
  if (!dateKey) return false
  return holidayDateSet.has(dateKey)
}

const isSelectableDate = (value, minimumDate, holidayDateSet, blockedDateSet) => {
  const parsedDate = parseDateValue(value)
  const dateKey = toDateKey(parsedDate)

  return (
    isValidDate(parsedDate) &&
    dateKey != '' &&
    !isWeekend(parsedDate) &&
    !isBeforeDate(parsedDate, minimumDate) &&
    !isHoliday(parsedDate, holidayDateSet) &&
    !blockedDateSet.has(dateKey)
  )
}

const getNextSelectableDate = (
  baseDate,
  minimumDate,
  holidayDateSet,
  blockedDateSet,
) => {
  const safeMinimumDate = isValidDate(minimumDate)
    ? toStartOfDay(minimumDate)
    : toStartOfDay(new Date())
  const parsedBaseDate = parseDateValue(baseDate)
  const nextDate = isValidDate(parsedBaseDate)
    ? toStartOfDay(parsedBaseDate)
    : new Date(safeMinimumDate)

  if (nextDate.getTime() < safeMinimumDate.getTime()) {
    nextDate.setTime(safeMinimumDate.getTime())
  }

  let attempts = 0

  while (
    !isSelectableDate(nextDate, safeMinimumDate, holidayDateSet, blockedDateSet)
  ) {
    nextDate.setDate(nextDate.getDate() + 1)
    attempts += 1

    if (attempts > 400) {
      return null
    }
  }

  return nextDate
}

const RescheduleModal = ({
  open,
  lang,
  initialSelectedDate,
  holidayDates = [],
  blockedDateKeys = [],
  onClose,
  onSubmit,
}) => {
  const today = useMemo(() => toStartOfDay(new Date()), [])
  const holidayDateSet = useMemo(
    () => normalizeHolidayDateSet(holidayDates),
    [holidayDates],
  )
  const blockedDateSet = useMemo(
    () => normalizeBlockedDateSet(blockedDateKeys),
    [blockedDateKeys],
  )

  const [draftSelectedDate, setDraftSelectedDate] = useState('')
  const [draftSelectedTime, setDraftSelectedTime] = useState(TIME_OPTIONS[0])
  const [draftNote, setDraftNote] = useState('')
  const [calendarMonth, setCalendarMonth] = useState(new Date())

  const draftSelectedDateObject = useMemo(
    () => parseDateValue(draftSelectedDate),
    [draftSelectedDate],
  )

  const minimumMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today],
  )

  const maximumMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + 11, 1),
    [today],
  )

  const calendarWeeks = useMemo(() => {
    if (!isValidDate(calendarMonth)) return []

    const year = calendarMonth.getFullYear()
    const month = calendarMonth.getMonth()
    const monthStart = new Date(year, month, 1)
    const monthEnd = new Date(year, month + 1, 0)
    const startDayIndex = (monthStart.getDay() + 6) % 7
    const totalDays = monthEnd.getDate()
    const calendarCells = []

    for (let index = 0; index < startDayIndex; index += 1) {
      calendarCells.push(null)
    }

    for (let day = 1; day <= totalDays; day += 1) {
      calendarCells.push(new Date(year, month, day))
    }

    while (calendarCells.length % 7 != 0) {
      calendarCells.push(null)
    }

    return Array.from({ length: calendarCells.length / 7 }, (_, weekIndex) =>
      calendarCells.slice(weekIndex * 7, weekIndex * 7 + 7),
    )
  }, [calendarMonth])

  const calendarMonthLabel = !isValidDate(calendarMonth)
    ? ''
    : (() => {
        const monthLabel = new Intl.DateTimeFormat(
          lang == 'id' ? 'id-ID' : 'en-US',
          {
            month: 'long',
            year: 'numeric',
          },
        ).format(calendarMonth)

        return monthLabel
          ? monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)
          : ''
      })()

  const canGoToPreviousMonth = useMemo(() => {
    if (!isValidDate(calendarMonth)) return false

    const currentMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      1,
    )

    return currentMonth > minimumMonth
  }, [calendarMonth, minimumMonth])

  const canGoToNextMonth = useMemo(() => {
    if (!isValidDate(calendarMonth)) return false

    const currentMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      1,
    )

    return currentMonth < maximumMonth
  }, [calendarMonth, maximumMonth])

  const submitDisabled =
    !draftSelectedDate || !draftSelectedTime || String(draftNote).trim() == ''

  useEffect(() => {
    if (!open) return

    const parsedInitialDate = parseDateValue(initialSelectedDate)
    const nextSelectedDate = getNextSelectableDate(
      parsedInitialDate,
      today,
      holidayDateSet,
      blockedDateSet,
    )
    const fallbackCalendarDate = nextSelectedDate || today

    setDraftSelectedDate(nextSelectedDate ? toDateKey(nextSelectedDate) : '')
    setDraftSelectedTime(TIME_OPTIONS[0])
    setDraftNote('')
    setCalendarMonth(
      new Date(
        fallbackCalendarDate.getFullYear(),
        fallbackCalendarDate.getMonth(),
        1,
      ),
    )
  }, [open, initialSelectedDate, today, holidayDateSet, blockedDateSet])

  useEffect(() => {
    if (!open) return undefined

    const originalOverflow = document.body.style.overflow
    const originalPaddingRight = document.body.style.paddingRight
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth

    document.body.style.overflow = 'hidden'

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.paddingRight = originalPaddingRight
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined

    const handleEscape = (event) => {
      if (event.key == 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [open, onClose])

  const handleDateSelect = (dateValue) => {
    if (!isSelectableDate(dateValue, today, holidayDateSet, blockedDateSet)) return
    setDraftSelectedDate(toDateKey(dateValue))
  }

  const handlePreviousMonth = () => {
    if (!canGoToPreviousMonth) return

    setCalendarMonth(
      (prevMonth) =>
        new Date(prevMonth.getFullYear(), prevMonth.getMonth() - 1, 1),
    )
  }

  const handleNextMonth = () => {
    if (!canGoToNextMonth) return

    setCalendarMonth(
      (prevMonth) =>
        new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 1),
    )
  }

  const handleSubmit = () => {
    onSubmit({
      requestedDate:
        draftSelectedDateObject && draftSelectedTime
          ? `${draftSelectedDate}T${draftSelectedTime}:00`
          : '',
      note: draftNote.trim(),
    })
  }

  if (!open) return null

  return (
    <div
      className='fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-3 sm:p-4'
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div className='flex w-full max-w-[400px] max-h-[calc(100dvh-24px)] flex-col overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)] sm:max-w-[440px] sm:max-h-[calc(100dvh-40px)] sm:rounded-[20px] lg:max-w-[470px]'>
        <div className='flex items-start justify-between gap-4 px-4 pt-4 sm:px-4 sm:pt-4 lg:px-5 lg:pt-5'>
          <div className='min-w-0'>
            <h3 className='text-[18px] font-normal leading-7 text-[#101828] sm:text-[19px] sm:leading-[28px] lg:text-[20px] lg:leading-[30px]'>
              {Lang(lang, 'request_reschedule')}
            </h3>
          </div>

          <button
            type='button'
            onClick={onClose}
            className='rounded-md p-1.5 text-[#667085] transition hover:bg-[#F8FAFC]'
          >
            <X className='h-5 w-5 sm:h-7 sm:w-7' strokeWidth={2} />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-5 sm:px-4 sm:pb-4 sm:pt-4 lg:px-5 lg:pb-5 lg:pt-5'>
          <div className='space-y-5 sm:space-y-5 lg:space-y-6'>
            <ApplicationNotice
              icon={<Clock3 className='h-[10px] w-[10px]' strokeWidth={1.8} />}
              tone='info'
            >
              <p>{Lang(lang, 'request_reschedule_notice')}</p>
            </ApplicationNotice>

            <div className='space-y-3 sm:space-y-4'>
              <h4 className='text-[15px] font-normal leading-6 text-[#374151] sm:text-[15px] sm:leading-6 lg:text-[16px] lg:leading-7'>
                {Lang(lang, 'select_reschedule_date')}
              </h4>

              <div className='rounded-[10px] border border-[#E5E7EB] px-3 py-3 sm:rounded-[12px] sm:px-3.5 sm:py-3.5 lg:px-4 lg:py-4'>
                <div className='flex items-center justify-between'>
                  <button
                    type='button'
                    onClick={handlePreviousMonth}
                    disabled={!canGoToPreviousMonth}
                    className='rounded-md p-1.5 text-[#98A2B3] transition hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <ChevronLeft
                      className='h-4 w-4 sm:h-5 sm:w-5'
                      strokeWidth={2}
                    />
                  </button>

                  <p className='text-[15px] font-medium leading-7 text-[#1F2937] sm:text-[16px] sm:font-bold sm:leading-7 lg:text-[17px]'>
                    {calendarMonthLabel}
                  </p>

                  <button
                    type='button'
                    onClick={handleNextMonth}
                    disabled={!canGoToNextMonth}
                    className='rounded-md p-1.5 text-[#98A2B3] transition hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <ChevronRight
                      className='h-4 w-4 sm:h-5 sm:w-5'
                      strokeWidth={2}
                    />
                  </button>
                </div>

                <div className='mt-3 grid grid-cols-7 gap-y-2.5 text-center sm:mt-3 sm:gap-y-2.5 lg:mt-4 lg:gap-y-3'>
                  {DAY_LABELS[lang == 'id' ? 'id' : 'en'].map((label) => (
                    <div
                      key={label}
                      className='text-[11px] font-normal leading-4 text-[#9CA3AF] sm:text-[11px] sm:leading-4 lg:text-[12px]'
                    >
                      {label}
                    </div>
                  ))}

                  {calendarWeeks.flat().map((dateValue, index) => {
                    if (!dateValue) {
                      return (
                        <div
                          key={`empty-${index}`}
                          className='h-8 sm:h-[34px] lg:h-[38px]'
                        />
                      )
                    }

                    const dateKey = toDateKey(dateValue)
                    const disabled = !isSelectableDate(
                      dateValue,
                      today,
                      holidayDateSet,
                      blockedDateSet,
                    )
                    const selected =
                      isValidDate(dateValue) &&
                      isValidDate(draftSelectedDateObject) &&
                      dateValue.getFullYear() ==
                        draftSelectedDateObject.getFullYear() &&
                      dateValue.getMonth() ==
                        draftSelectedDateObject.getMonth() &&
                      dateValue.getDate() == draftSelectedDateObject.getDate()

                    return (
                      <button
                        key={dateKey}
                        type='button'
                        disabled={disabled}
                        onClick={() => handleDateSelect(dateValue)}
                        className={`mx-auto flex h-[32px] w-[36px] items-center justify-center rounded-[8px] text-[13px] font-normal leading-5 transition sm:h-[34px] sm:w-[34px] sm:rounded-[8px] sm:text-[13px] sm:leading-5 lg:h-[38px] lg:w-[38px] lg:rounded-[10px] lg:text-[14px] lg:leading-6 ${
                          selected
                            ? 'bg-[#F1F5F9] text-[#EA2427]'
                            : disabled
                              ? 'cursor-not-allowed text-[#D1D5DB]'
                              : 'text-[#020817] hover:bg-[#FFF1F2]'
                        }`}
                      >
                        {dateValue.getDate()}
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>

            <div className='space-y-3 sm:space-y-4'>
              <div>
                <h4 className='text-[15px] font-normal leading-6 text-[#374151] sm:text-[15px] sm:leading-6 lg:text-[16px] lg:leading-7'>
                  {Lang(lang, 'select_interview_time')}
                </h4>
                <p className='mt-1 text-[11px] font-normal leading-4 text-[#64748B] sm:text-[11px] sm:leading-4 lg:text-[12px] lg:leading-5'>
                  {Lang(lang, 'reschedule_time_helper')}
                </p>
              </div>

              <div className='mt-3 flex flex-wrap gap-2 sm:gap-2.5'>
                {TIME_OPTIONS.map((timeValue) => {
                  const isSelected = draftSelectedTime == timeValue

                  return (
                    <button
                      key={timeValue}
                      type='button'
                      onClick={() => setDraftSelectedTime(timeValue)}
                      className={`flex min-h-[36px] min-w-[62px] flex-col items-center justify-center rounded-[8px] border px-3 py-2 transition sm:min-h-[38px] sm:min-w-[66px] sm:rounded-[8px] sm:px-3 sm:py-2 lg:min-h-[40px] lg:min-w-[72px] lg:rounded-[10px] lg:px-4 lg:py-2 ${
                        isSelected
                          ? 'border-[#EA2427] bg-[#EA2427]'
                          : 'border-[#E5E7EB] bg-white hover:border-[#FCA5A5]'
                      }`}
                    >
                      <span
                        className={`leading-5 ${
                          isSelected
                            ? 'text-[12px] font-medium text-[#F8FAFC] sm:text-[12px] sm:leading-5 lg:text-[13px] lg:leading-6'
                            : 'text-[12px] font-normal text-[#020817] sm:text-[12px] sm:leading-5 lg:text-[13px] lg:leading-6'
                        }`}
                      >
                        {timeValue}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div className='space-y-3 sm:space-y-4'>
              <label className='block text-[15px] font-normal leading-6 text-[#374151] sm:text-[15px] sm:leading-6 lg:text-[16px] lg:leading-7'>
                {Lang(lang, 'reschedule_note')}{' '}
                <span className='text-[#EA2427]'>*</span>
              </label>

              <textarea
                value={draftNote}
                onChange={(event) => setDraftNote(event.target.value)}
                placeholder={Lang(lang, 'reschedule_note_placeholder')}
                className='min-h-[96px] w-full rounded-[10px] border border-[#E5E7EB] px-3 py-3 text-[13px] font-normal leading-5 text-[#111827] outline-none transition placeholder:text-[#98A2B3] focus:border-[#FCA5A5] sm:min-h-[104px] sm:rounded-[12px] sm:px-3 sm:py-3 sm:text-[13px] sm:leading-5 lg:min-h-[112px] lg:px-4 lg:py-3.5 lg:text-[14px] lg:leading-6'
              />
            </div>
          </div>
        </div>

        <div className='flex flex-row gap-2.5 border-t border-[#E2E8F0] px-4 py-4 sm:px-4 sm:py-3 lg:px-5 lg:py-4'>
          <button
            type='button'
            onClick={onClose}
            className='min-w-0 basis-[38%] rounded-full border border-[#D0D5DD] px-4 py-2.5 text-[13px] font-semibold leading-5 text-[#344054] sm:min-w-[104px] sm:basis-auto sm:px-4 sm:py-2.5 sm:text-[13px] sm:leading-5 lg:min-w-[116px] lg:px-5 lg:text-[14px] lg:leading-6'
          >
            {Lang(lang, 'cancel')}
          </button>

          <button
            type='button'
            onClick={handleSubmit}
            disabled={submitDisabled}
            className='min-w-0 basis-[62%] rounded-full bg-[#EA2427] px-4 py-2.5 text-[13px] font-semibold leading-5 text-white transition hover:bg-[#D91F22] disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[148px] sm:basis-auto sm:px-4 sm:py-2.5 sm:text-[13px] sm:leading-5 lg:min-w-[164px] lg:px-5 lg:text-[14px] lg:leading-6'
          >
            {Lang(lang, 'submit_reschedule_request')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RescheduleModal
